import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, ShieldCheck, CalendarCheck, BellRing,
  Database, RefreshCw, Layers, Plug, Zap,
} from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";
import { EMRS } from "@/config/emrs";
import { IntegrationsSlider } from "@/components/landing/IntegrationsSlider";
import { INTEGRATIONS, integrationTestId } from "@/config/integrations";
import { NotifyEmrModal } from "@/components/landing/NotifyEmrModal";
import { PainPoints, DATA_STATS } from "@/components/welcome/PainPoints";
import { DiscChapters } from "@/components/welcome/DiscChapters";
import { INTEGRATIONS_CHAPTERS } from "@/data/discChapters";

/* Group the master integrations list by category so the page reads as a directory. */
const CATEGORY_ORDER = [
  "Calendars",
  "Video & Voice",
  "Social",
  "Ads & Marketing",
  "Payments",
  "Accounting",
  "E-commerce",
  "Productivity",
  "Automation",
];

const TOOLS_BY_CATEGORY = CATEGORY_ORDER
  .map((cat) => ({ category: cat, items: INTEGRATIONS.filter((t) => t.category === cat) }))
  .filter((g) => g.items.length > 0);

const BRIDGE_FEATURES = [
  { icon: RefreshCw, title: "Two-way sync", body: "Bookings made by AI write back to your EMR's schedule. Edits in your EMR reflect in Clinic OS instantly." },
  { icon: Database,  title: "One patient record", body: "Charts, appointments, balances and tags stay aligned — never copy-pasted." },
  { icon: ShieldCheck, title: "HIPAA-grade transport", body: "TLS in transit, encrypted at rest, full audit log per record write." },
  { icon: Zap,       title: "Real-time webhooks",   body: "Sub-second propagation. Calls picked up by AI are reflected in the EMR within seconds." },
];

export default function Integrations() {
  const openDemo = useDemo();
  const [notifySlug, setNotifySlug] = useState(null);

  const liveEMRs = EMRS.filter((e) => e.status === "live");
  const soonEMRs = EMRS.filter((e) => e.status === "soon");

  return (
    <article data-testid="integrations-page" className="overflow-hidden">
      {/* ───────────── HERO ───────────── */}
      <section className="relative pt-16 pb-8 sm:pt-40 sm:pb-16">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[520px] bg-gradient-to-b from-cyan-500/15 via-blue-500/8 to-transparent blur-3xl" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <p className="hidden sm:inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-600 dark:text-cyan-400">
            <span className="h-1.5 w-1.5 rounded-full bg-cyan-500" />
            Integrations
          </p>
          <h1 className="mt-0 sm:mt-5 max-w-4xl font-display text-[2.05rem] font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            One bridge between your EMR{" "}
            <span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-violet-500 bg-clip-text text-transparent">
              and every tool you run on.
            </span>
          </h1>
          <p className="mt-3 sm:mt-5 max-w-3xl text-[14px] leading-relaxed text-muted-foreground sm:text-lg lg:text-xl">
            Clinic OS sits between your EMR and the dozen tools your front desk juggles — and keeps them
            all in real-time sync. Native to <strong className="font-semibold text-foreground/85">Juvonno</strong>{" "}
            and <strong className="font-semibold text-foreground/85">Jane App</strong> today, with more
            EMRs landing every quarter.
          </p>

          <div className="mt-5 sm:mt-8 flex flex-wrap items-center gap-2 sm:gap-3">
            <a
              href="#flagship-emrs"
              data-testid="integrations-jump-emrs"
              className="group inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-6 text-sm font-bold text-white shadow-lg shadow-blue-600/25 transition-all hover:-translate-y-0.5 hover:shadow-xl"
            >
              <Plug className="h-4 w-4" />
              See your EMR
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              href="#tools"
              data-testid="integrations-jump-tools"
              className="hidden sm:inline-flex h-12 items-center gap-2 rounded-xl border border-border bg-card px-5 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:bg-secondary"
            >
              <Layers className="h-4 w-4 text-violet-500" />
              See connected tools
            </a>
            <button
              type="button"
              onClick={openDemo}
              data-testid="integrations-book-demo"
              className="hidden sm:inline-flex h-12 items-center gap-1 px-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              <CalendarCheck className="h-4 w-4" />
              Book a Demo
            </button>
          </div>

          {/* Trust strip — desktop only */}
          <div className="mt-6 hidden flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-muted-foreground sm:flex">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> HIPAA &amp; SOC 2
            </span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span className="inline-flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5 text-cyan-500" /> Real-time two-way sync
            </span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span>No data lock-in</span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span>Live in &lt; 14 days</span>
          </div>
        </div>

        {/* Logo marquee — desktop only; mobile users scroll to the EMR list directly */}
        <div className="mx-auto mt-12 hidden max-w-7xl px-6 sm:block sm:px-8 lg:px-12">
          <IntegrationsSlider />
        </div>
      </section>

      {/* ───── DiSC PAIN-MAPPING ───── */}
      <div className="border-t border-border/60 pt-10 sm:pt-14">
        <PainPoints
          eyebrow="The integration story in numbers"
          heading="Stop bolting tools on. Start owning the data."
          sub="Three numbers that change when your EMR talks 2-way with the rest of your stack."
          stats={DATA_STATS}
        />
        <DiscChapters
          eyebrow="Integrations, four ways"
          heading="Pick the chapter that sounds like your stack."
          sub="EMR-first, comms-first, hands-on migration, or open-architecture — every integration story, told four ways."
          chapters={INTEGRATIONS_CHAPTERS}
        />
      </div>

      {/* ───────────── FLAGSHIP EMRs — Juvonno & Jane App ───────────── */}
      <section
        id="flagship-emrs"
        data-testid="integrations-emrs"
        className="relative overflow-hidden border-t border-border bg-gradient-to-b from-slate-50/60 via-white to-slate-50/60 py-20 dark:bg-none dark:from-transparent dark:via-transparent dark:to-transparent sm:py-24"
      >
        <div className="pointer-events-none absolute -left-32 top-20 h-72 w-72 rounded-full bg-cyan-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -right-32 bottom-20 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.22em] text-cyan-600 dark:text-cyan-400">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-500" />
              </span>
              Live · native 2-way sync
            </p>
            <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl">
              Two flagship EMRs.{" "}
              <span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-violet-500 bg-clip-text text-transparent">Native integration.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
              Our deepest integrations are with the two EMRs most Canadian and US clinics already run on.
              Real-time, in both directions — no Zapier middleware, no overnight CSV jobs.
            </p>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-2">
            {liveEMRs.map((emr) => (
              <motion.div
                key={emr.slug}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5 }}
                data-testid={`integrations-emr-${emr.slug}`}
                className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-foreground/25 hover:shadow-lg"
              >
                {/* corner glow in EMR's accent color */}
                <div className={`pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-gradient-to-br ${emr.accent} opacity-[0.20] blur-3xl`} />

                {/* Status pill */}
                <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-emerald-600 dark:text-emerald-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Live
                </span>

                <div className="relative flex items-start gap-4 pr-16">
                  <span className="inline-flex h-14 w-14 flex-none items-center justify-center rounded-2xl border border-border bg-white shadow-sm ring-1 ring-inset ring-black/5">
                    <img src={emr.logo} alt={emr.name} className="h-8 w-8 object-contain" />
                  </span>
                  <div>
                    <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Native EMR · deep sync</p>
                    <h3 className="font-display text-2xl font-black tracking-tight sm:text-3xl">{emr.name}</h3>
                    <p className="mt-0.5 text-[12.5px] leading-snug text-muted-foreground">{emr.tagline}</p>
                  </div>
                </div>

                <p className="relative mt-5 text-[14px] leading-relaxed text-foreground/85">
                  {emr.short}
                </p>

                {emr.suited && (
                  <div className="relative mt-5">
                    <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Best for</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {emr.suited.map((s) => (
                        <span key={s} className="inline-flex items-center rounded-full border border-border bg-secondary/40 px-2 py-0.5 text-[11px] font-semibold text-foreground/75">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="relative mt-7 flex flex-wrap items-center gap-2">
                  <Link
                    to={`/integrations/${emr.slug}`}
                    data-testid={`integrations-emr-${emr.slug}-cta`}
                    className={`group/btn inline-flex h-10 items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r ${emr.accent} px-4 text-[13px] font-bold text-white shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg`}
                  >
                    Explore {emr.name} integration
                    <ArrowRight className="h-3 w-3 transition-transform group-hover/btn:translate-x-0.5" />
                  </Link>
                </div>

                {/* Bottom accent bar */}
                <div className={`absolute inset-x-0 bottom-0 h-[3px] bg-gradient-to-r ${emr.accent}`} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── BRIDGE STORY — How it works ───────────── */}
      <section data-testid="integrations-bridge" className="relative overflow-hidden border-t border-border py-20 sm:py-24">
        <div className="pointer-events-none absolute -right-32 top-20 h-72 w-72 rounded-full bg-violet-500/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
              How the bridge works
            </p>
            <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl">
              Records stay clean.{" "}
              <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">Both sides. Always.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
              The hardest part of EMR integration isn&apos;t the API — it&apos;s keeping two databases honest with each other every second of every day. We&apos;ve solved that.
            </p>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {BRIDGE_FEATURES.map((b, i) => {
              const BIcon = b.icon;
              return (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-foreground/25 hover:shadow-lg"
                >
                  <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br from-cyan-500 to-violet-500 opacity-[0.08] blur-3xl transition-opacity duration-300 group-hover:opacity-[0.20]" />

                  <span className="relative inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 via-blue-500 to-violet-500 text-white shadow-sm ring-1 ring-inset ring-white/15">
                    <BIcon className="h-5 w-5" />
                  </span>
                  <p className="relative mt-4 font-display text-[15px] font-black tracking-tight">{b.title}</p>
                  <p className="relative mt-1.5 text-[12.5px] leading-relaxed text-muted-foreground">{b.body}</p>

                  <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.6, delay: 0.15 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-x-0 bottom-0 h-[3px] origin-left bg-gradient-to-r from-cyan-500 to-violet-500"
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ───────────── TOOLS GRID ───────────── */}
      <section
        id="tools"
        data-testid="integrations-tools"
        className="relative overflow-hidden border-t border-border bg-secondary/30 py-20 dark:bg-transparent sm:py-24"
      >
        <div className="pointer-events-none absolute -left-32 top-20 h-72 w-72 rounded-full bg-blue-500/12 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/30 bg-blue-500/10 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.22em] text-blue-600 dark:text-blue-300">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
              Connected tools
            </p>
            <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl">
              The {INTEGRATIONS.length}+ tools your front desk{" "}
              <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">already lives in.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
              Calendars, payments, ads, social, accounting, automation — every channel piped into one unified inbox and patient record. No more tab-switching tax.
            </p>
          </div>

          <div className="mt-12 space-y-10">
            {TOOLS_BY_CATEGORY.map((group, gi) => (
              <motion.div
                key={group.category}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, delay: gi * 0.04 }}
                data-testid={`integrations-category-${integrationTestId(group.category)}`}
              >
                <div className="mb-4 flex items-baseline justify-between gap-3">
                  <h3 className="font-display text-[15px] font-black uppercase tracking-[0.18em] text-foreground/85">
                    {group.category}
                  </h3>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {group.items.length} {group.items.length === 1 ? "tool" : "tools"}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                  {group.items.map((t, i) => (
                    <motion.div
                      key={t.name}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.3, delay: i * 0.02 }}
                      data-testid={`integration-tool-${integrationTestId(t.name)}`}
                      className="group flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 transition-all hover:-translate-y-0.5 hover:border-foreground/25 hover:shadow-md"
                    >
                      <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-white shadow-inner ring-1 ring-inset ring-black/5">
                        <img
                          src={t.logo}
                          alt={`${t.name} logo`}
                          loading="lazy"
                          className="h-5 w-5 object-contain"
                          onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                        />
                      </span>
                      <span className="truncate text-[13px] font-semibold tracking-tight text-foreground/85">{t.name}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── MORE EMRs COMING SOON ───────────── */}
      {soonEMRs.length > 0 && (
        <section
          data-testid="integrations-emrs-soon"
          className="border-t border-border py-20 sm:py-24"
        >
          <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
            <div className="max-w-2xl">
              <p className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.22em] text-amber-600 dark:text-amber-400">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                Coming next
              </p>
              <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl">
                More EMRs landing{" "}
                <span className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 bg-clip-text text-transparent">every quarter.</span>
              </h2>
              <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
                We&apos;re building deep, native 2-way integrations with the next wave. Add your name to the list and we&apos;ll ping you the day it ships.
              </p>
            </div>

            <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
              {soonEMRs.map((emr) => (
                <button
                  type="button"
                  key={emr.slug}
                  onClick={() => setNotifySlug(emr.slug)}
                  data-testid={`integrations-emr-soon-${emr.slug}`}
                  className="group relative flex items-center gap-3 overflow-hidden rounded-xl border border-dashed border-border bg-card/60 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-amber-500/40 hover:bg-card hover:shadow-sm"
                >
                  <span className="inline-flex h-11 w-11 flex-none items-center justify-center rounded-lg bg-white ring-1 ring-inset ring-black/5">
                    <img src={emr.logo} alt={emr.name} className="h-6 w-6 object-contain" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-[14px] font-bold tracking-tight">{emr.name}</p>
                    <span className="mt-0.5 inline-flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.14em] text-amber-600 dark:text-amber-400 group-hover:text-amber-500">
                      <BellRing className="h-2.5 w-2.5" />
                      Notify me when live
                    </span>
                  </div>
                  <ArrowRight className="h-3.5 w-3.5 flex-none text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-amber-500" />
                </button>
              ))}
            </div>

            {/* "Don't see yours?" card */}
            <button
              type="button"
              onClick={openDemo}
              data-testid="integrations-request-emr"
              className="group mt-10 flex w-full items-center justify-between gap-4 rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/[0.08] via-blue-500/[0.04] to-emerald-500/[0.08] p-5 text-left transition-all hover:-translate-y-0.5 hover:border-violet-500/55 hover:shadow-md hover:shadow-violet-500/15 sm:p-6"
            >
              <div className="flex items-start gap-4">
                <span className="inline-flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-md shadow-violet-600/30">
                  <Sparkles className="h-5 w-5" />
                </span>
                <div>
                  <p className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300">
                    Don&apos;t see your EMR?
                  </p>
                  <p className="mt-1 font-display text-lg font-black leading-tight tracking-tight sm:text-xl">
                    Tell us — most integrations are 30 days away.
                  </p>
                  <p className="mt-1 text-[13px] leading-snug text-muted-foreground">
                    Book a 20-minute call. We&apos;ll review your EMR and quote a build timeline.
                  </p>
                </div>
              </div>
              <span className="hidden flex-none items-center gap-1.5 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-2.5 text-[12.5px] font-bold text-white shadow-md shadow-violet-600/25 transition-all group-hover:-translate-y-0.5 group-hover:shadow-lg sm:inline-flex">
                Book a Demo
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </button>
          </div>
        </section>
      )}

      {/* ───────────── FINAL CTA — dark cinematic ───────────── */}
      <section className="border-t border-border bg-gradient-to-b from-white via-slate-50/60 to-slate-100/60 py-16 dark:bg-none dark:from-transparent dark:via-transparent dark:to-transparent sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl border border-border bg-[#0a0a14] px-8 py-16 text-center sm:px-16 sm:py-24">
            <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-cyan-500/30 blur-[120px]" />
            <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-violet-600/25 blur-[120px]" />
            <div className="pointer-events-none absolute inset-0 bg-grid opacity-30" />
            <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/5" />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75 backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                Native EMRs · {INTEGRATIONS.length}+ tool integrations · 14-day go-live
              </span>

              <h2 className="mt-6 font-display text-4xl font-black leading-[1.05] tracking-[-0.025em] text-white sm:text-6xl lg:text-7xl">
                Plug Clinic OS in.{" "}
                <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400 bg-clip-text text-transparent">
                  Keep your stack.
                </span>
              </h2>

              <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
                You don&apos;t need to switch EMRs. You don&apos;t need to abandon tools your team already loves.
                Clinic OS sits in the middle and keeps everything honest.
              </p>

              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={openDemo}
                  data-testid="integrations-final-demo"
                  className="group inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-8 text-sm font-bold text-white shadow-lg shadow-blue-600/40 ring-1 ring-inset ring-white/10 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-600/50 sm:w-auto"
                >
                  <CalendarCheck className="h-4 w-4" /> Book a Demo
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </button>
                <Link
                  to="/build"
                  data-testid="integrations-final-build"
                  className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-8 text-sm font-semibold text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/10 sm:w-auto"
                >
                  Build Your OS
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              <div className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-white/55">
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                  HIPAA-ready
                </span>
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span>SOC 2 aligned</span>
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span>Two-way sync</span>
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span>No data lock-in</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Notify-me modal for SOON EMRs (Clinicko, PT Everywhere, …) */}
      <NotifyEmrModal
        open={!!notifySlug}
        slug={notifySlug}
        onClose={() => setNotifySlug(null)}
      />
    </article>
  );
}
